import { customersRepository } from "@/database/repositories/customersRepository";
import { CustomerWithContacts } from "@/database/schema";
import { useEffect, useState } from "react";
import { $archivedCustomers, $customers } from "@/stores";
import { ConfirmRestore } from "@/shared/confirmRestore/ConfirmRestore";
import { Flip, toast, ToastContainer } from "react-toastify";
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    TableMeta,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import { DataTableToolbar } from "@/shared/table/TableToolbar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/table/TableElements";
import { DataTablePagination } from "@/shared/table/TablePagination";
import { countries } from "@/shared/countriesDropdown/countries";
import "./customersArchive.scss";
import { Undo2 } from "lucide-react";
import { archivedCustomersColumns } from "./ArchiveCustomersColumns";
import { TableFilterOption } from "@/types";
import { useSearchParams } from "react-router-dom";
import { useDb } from "@/database/provider/useDb";

export interface ArchivedCustomersTableMeta extends TableMeta<CustomerWithContacts> {
    restoreCustomer: (id: number) => Promise<void>,
}

export const CustomersArchive = () => {
    const db = useDb();
    const [searchParams] = useSearchParams();
    const [customers, setCustomers] = useState<CustomerWithContacts[]>([]);
    const [isConfirmRestore, setIsConfirmRestore] = useState(false);
    const [rowSelection, setRowSelection] = useState({});
    const [columnSizing, setColumnSizing] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterCountries: TableFilterOption[] = countries.map(({ name }: { name: string }) => {
        return { value: name, text: name }
    });

    const restoreCustomer = async (id: number) => {
        try {
            await customersRepository.restore(db, id);
            const index = customers.findIndex(item => item.id === id);
            if (index !== -1) {

                const removedCustomer = customers.splice(index, 1)[0];
                $customers.value.values.push(removedCustomer);
                setCustomers(customers.filter((item) => item.id !== id));

                toast.success("Customer restored!", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: false,
                    pauseOnHover: true,
                    theme: "light",
                    transition: Flip,
                });
            }
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }


    const table = useReactTable({
        data: customers,
        columns: archivedCustomersColumns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing
        },
        meta: {
            type: "Customers",
            restoreCustomer
        } as ArchivedCustomersTableMeta,
        enableRowSelection: true,
        onColumnSizingChange: setColumnSizing,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const restoreMany = async () => {
        try {
            const ids = table.getSelectedRowModel().rows.map(row => row.original.id);
            await customersRepository.restoreMany(db, ids);
            const { values } = $customers.value;
            const restored = $archivedCustomers.value.values.filter((item) => ids.includes(item.id));
            $customers.set({ isLoad: true, values: values.concat(restored) });

            let { values: archived } = $archivedCustomers.value;
            archived = archived.filter((item) => !ids.includes(item.id));

            $archivedCustomers.set({ isLoad: true, values: archived });
            setCustomers(archived);

            toast.success("Customers restored!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }
    useEffect(() => {
        try {
            if (!$archivedCustomers.value.isLoad) {
                customersRepository.getAllArchived(db).then((res) => {
                    setCustomers(res);
                    $archivedCustomers.set({ isLoad: true, values: res });
                })
            }
            else setCustomers($archivedCustomers.value.values);

            const countriesFilters = searchParams.get("country");

            if (countriesFilters) {
                const statusesArray = countriesFilters.split(",");
                const column = table.getColumn("country");
                column?.setFilterValue(statusesArray);
            }
        }
        catch (err: any) {
            throw new Error(err.message);
        }
    }, [db]);

    return <section className="customers-archive">
        <div className="customers-archive__container">
            <ToastContainer />
            {isConfirmRestore && <div className="overlay">
                <ConfirmRestore
                    type="Customer"
                    count={table.getSelectedRowModel().rows.length}
                    onClose={() => { setIsConfirmRestore(false) }}
                    onRestore={() => { restoreMany() }} />
            </div>}

            <div className="table-container">
                <div className="table-container__data">
                    <div className="table-container__head">
                        <div className="table__head-container">
                            <h1>Archived Customers</h1>
                        </div>
                        <div className="table__toolbar-container">
                            <DataTableToolbar table={table}
                                filtersFields={[{ field: "country", title: "Country", options: filterCountries }]} searchField="name" />
                            {table.getSelectedRowModel().rows.length > 0 &&
                                <button className="table__restore-button" onClick={() => setIsConfirmRestore(true)}>
                                    <Undo2 width={14} height={14} /> Restore {table.getSelectedRowModel().rows.length} items
                                </button>
                            }
                        </div>
                    </div>
                    <div className="table-container__table-area">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <TableHead key={header.id} style={{ width: `${header.getSize()}%` }}>
                                                    {header.isPlaceholder ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            )
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => {
                                                if (table.getSelectedRowModel().rows.length > 0) {
                                                    return row.toggleSelected()
                                                }
                                            }}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="table__cell"
                                                    style={{ width: `${cell.column.getSize()}%` }}
                                                    onClick={(e) => {
                                                        const { id } = cell.column;
                                                        if (id === "select" || id === "actions") e.stopPropagation()
                                                    }}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <div className="empty">
                                        <div className="empty__inner">
                                            <img src="/Eyes.png" alt="No Data" />
                                            <b>No results</b>
                                        </div>
                                    </div>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <div className="table-container__pagination">
                    <DataTablePagination table={table} />
                </div>
            </div>
        </div>
    </section>
}
